import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { ArrowRight, Loader2 } from 'lucide-react';
import { productsStore, initializeCache, Product } from '@/stores/localStorageStore';

const FeaturedProducts = () => {
  const { t } = useLanguage();
  const getFeaturedFromCache = () => {
    const allProducts = productsStore.getAll();
    const featured = allProducts.filter(p => p.isFeatured && p.status !== 'draft' && p.status !== 'pos_only');
    return featured.length > 0 ? featured.slice(0, 8) : [];
  };

  const initialProducts = getFeaturedFromCache();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>(initialProducts);
  const [isLoading, setIsLoading] = useState(productsStore.getAll().length === 0);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        if (productsStore.getAll().length === 0) {
          setIsLoading(true);
        }
        await initializeCache();
        setFeaturedProducts(getFeaturedFromCache());
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  if (isLoading) {
    return (
      <section id="featured-products" className="bg-secondary py-20">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (featuredProducts.length === 0) {
    return null;
  }

  return (
    <section id="featured-products" className="bg-background py-16 md:py-24 relative">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 flex flex-col items-center text-center"
        >
          <span className="mb-4 inline-block rounded-full bg-primary/5 border border-primary/10 px-4 py-1.5 text-[13px] font-semibold tracking-wider uppercase text-primary">
            {t('ourSelection') || 'Nasz wybór'}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-[56px] font-bold tracking-tight text-foreground">
            {t('featuredProducts') || 'Polecane produkty'}
          </h2>

          {/* Grape separator */}
          <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-3 my-6"
          >
              <div className="h-[1.5px] w-16 bg-primary/30" />
              <div className="text-primary/70">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 5V2l-5.89 5.89"/><circle cx="16.6" cy="15.89" r="3"/><circle cx="8.11" cy="7.4" r="3"/><circle cx="12.35" cy="11.65" r="3"/><circle cx="13.91" cy="5.85" r="3"/><circle cx="18.15" cy="10.09" r="3"/><circle cx="6.56" cy="13.2" r="3"/><circle cx="10.8" cy="17.44" r="3"/><circle cx="5" cy="19" r="3"/></svg>
              </div>
              <div className="h-[1.5px] w-16 bg-primary/30" />
          </motion.div>

          <p className="mx-auto max-w-2xl text-[15px] sm:text-base text-muted-foreground leading-relaxed">
            {t('featuredDescription') || 'Odkryj nasze najlepsze produkty - starannie wyselekcjonowane wina i włoskie specjały'}
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {featuredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="h-full"
            >
              <ProductCard
                {...product}
                name={product.name}
                price={product.price}
                description={product.description || ''}
                descriptionEn={product.descriptionEn || ''}
                image={product.image || product.images?.[0] || ''}
                minimal={true}
              />
            </motion.div>
          ))}
        </div>

        {/* Show More Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Link
            to="/produkty"
            className="group inline-flex items-center gap-2 sm:gap-3 rounded-full border-2 border-primary bg-transparent px-5 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold uppercase tracking-wider text-primary transition-all hover:bg-primary hover:text-primary-foreground"
          >
            <span>{t('showMore') || 'Zobacz wszystkie produkty'}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
